// =====================================================================
// tpCustomer — Sales -> Partners
//
// Partners are accounts where `type = 'Partner'`, with a small extension
// table for tier / agreement / commission / MDF. The list view shows the
// joined view of accounts + partners; the detail view reuses the
// existing AccountDetail page (clicking a partner row navigates there,
// where a "Partner" tab surfaces the partner-specific fields when
// the account's type is Partner).
//
// Add-partner flow:
//   1. Pick an existing account (or create a new one as type=Partner)
//   2. The dialog flips accounts.type='Partner' if needed, then upserts
//      the partners extension row with tier / commission / etc.
// =====================================================================

function PartnersList({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  const [tierFilter, setTierFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const partners = D.PARTNERS || [];

  const rows = partners.filter(p => {
    if (tierFilter !== "all" && p.tier !== tierFilter) return false;
    if (typeFilter !== "all" && p.partnerType !== typeFilter) return false;
    return true;
  });

  // Aggregate quick stats per partner from opps where this partner is
  // attached. Cheap O(opp × 1) scan; the opportunity list is small.
  const opps = D.OPPORTUNITIES || [];
  function statsFor(accountId) {
    const myOpps = opps.filter(o => o.partnerAccountId === accountId);
    const open = myOpps.filter(o => !(o.stage || "").startsWith("Closed"));
    const won  = myOpps.filter(o => o.stage === "Closed Won");
    const wonAcv = won.reduce((s, o) => s + (o.acv || 0), 0);
    return { openCount: open.length, wonCount: won.length, wonAcv };
  }

  return (
    <>
      <PageHead
        title="Partners"
        sub={`${rows.length} partner${rows.length === 1 ? "" : "s"} · ${partners.length} total`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ Add partner</button>}
      />

      <div className="card">
        <FilterToolbar count={rows.length} total={partners.length}>
          <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
            <option value="all">All tiers</option>
            {(D.PARTNER_TIERS || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            {(D.PARTNER_TYPES || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </FilterToolbar>
        {rows.length === 0 ? (
          <div className="card-body muted">
            {partners.length === 0
              ? "No partners yet. Use “+ Add partner” to flip an account to type=Partner and set its tier / commission."
              : "No partners match these filters."}
          </div>
        ) : (
          <table className="tbl tbl-flush">
            <thead><tr>
              <th>Partner</th><th>Tier</th><th>Type</th><th>Region</th>
              <th className="right">Default %</th>
              <th className="right">Open deals</th>
              <th className="right">Won ACV</th>
              <th>Agreement</th>
            </tr></thead>
            <tbody>
              {rows.map(p => {
                const s = statsFor(p.accountId);
                return (
                  <tr key={p.accountId} onClick={() => navigate("sales/accounts/" + p.accountId)}>
                    <td><span className="tbl-link">{p.accountName}</span></td>
                    <td>{p.tier ? <Badge tone={p.tier === "Platinum" ? "amber" : p.tier === "Gold" ? "amber" : p.tier === "Silver" ? "slate" : "slate"}>{p.tier}</Badge> : <span className="muted">—</span>}</td>
                    <td>{p.partnerType || <span className="muted">—</span>}</td>
                    <td className="muted">{p.accountRegion || "—"}</td>
                    <td className="right numeric">{p.defaultCommissionPct != null ? p.defaultCommissionPct + "%" : <span className="muted">—</span>}</td>
                    <td className="right numeric">{s.openCount || <span className="muted">0</span>}</td>
                    <td className="right numeric">{s.wonAcv ? fmtMoney(s.wonAcv) : <span className="muted">—</span>}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {p.agreementStart || p.agreementEnd
                        ? `${fmtDateShort(p.agreementStart) || "—"} → ${fmtDateShort(p.agreementEnd) || "—"}`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewPartnerDialog onClose={() => setShowNew(false)} navigate={navigate} />}
    </>
  );
}

// New-partner dialog. Picks an existing account (any type), flips its
// type to Partner, then upserts the partner extension row. Saves in
// two API calls — order matters because the partner-upsert endpoint
// rejects accounts whose type isn't yet Partner.
function NewPartnerDialog({ onClose, navigate }) {
  const accounts = (D.ACCOUNTS || []).filter(a => a.type !== "Customer"); // most partners aren't existing customers; let user override below
  const allAccounts = D.ACCOUNTS || [];
  const partnersById = new Set((D.PARTNERS || []).map(p => p.accountId));

  const [accountId, setAccountId] = useState("");
  const [tier, setTier] = useState("Silver");
  const [partnerType, setPartnerType] = useState("Reseller");
  const [defaultCommissionPct, setDefaultCommissionPct] = useState("15");
  const [agreementStart, setAgreementStart] = useState("");
  const [agreementEnd, setAgreementEnd] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const candidates = (showAllAccounts ? allAccounts : accounts).filter(a => !partnersById.has(a.id));

  async function handleSave() {
    if (!accountId) { setError("Pick an account."); return; }
    setSaving(true); setError(null);
    try {
      const acct = allAccounts.find(a => a.id === accountId);
      if (!acct) throw new Error("Account not found in local cache. Reload and try again.");
      // Flip type to Partner first (controller rejects partner-upsert otherwise).
      if (acct.type !== "Partner") {
        await tpAPI.accounts.update(accountId, { type: "Partner" });
      }
      await tpAPI.partners.save(accountId, {
        tier:        tier        || null,
        partnerType: partnerType || null,
        defaultCommissionPct: defaultCommissionPct === "" ? null : Number(defaultCommissionPct),
        agreementStart: agreementStart || null,
        agreementEnd:   agreementEnd   || null,
        portalUrl: portalUrl.trim() || null,
        notes:     notes.trim()     || null,
      });
      onClose();
      navigate("sales/accounts/" + accountId);
    } catch (e) {
      setError(e.message || "Failed to save partner.");
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Add partner"
      onClose={onClose}
      footer={<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Add partner"}
        </button>
      </>}
    >
      <div className="form-field">
        <label>Account *</label>
        <select value={accountId} onChange={e => setAccountId(e.target.value)} autoFocus>
          <option value="">— Select an account —</option>
          {candidates.map(a => (
            <option key={a.id} value={a.id}>
              {a.name} {a.type && a.type !== "Partner" ? ` (currently ${a.type})` : ""}
            </option>
          ))}
        </select>
        {!showAllAccounts && (
          <label style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, display: "block" }}>
            <input type="checkbox" checked={showAllAccounts} onChange={() => setShowAllAccounts(true)} style={{ marginRight: 4 }} />
            Show customers too (a partner can also be a customer)
          </label>
        )}
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Tier</label>
          <select value={tier} onChange={e => setTier(e.target.value)}>
            <option value="">— None —</option>
            {(D.PARTNER_TIERS || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Type</label>
          <select value={partnerType} onChange={e => setPartnerType(e.target.value)}>
            <option value="">— None —</option>
            {(D.PARTNER_TYPES || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Default commission %</label>
          <input
            type="number" min="0" max="100" step="0.5"
            value={defaultCommissionPct}
            onChange={e => setDefaultCommissionPct(e.target.value)}
            placeholder="15"
          />
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Seeds the partner commission on each opp where this partner is attached. Per-deal overrides still apply.
          </div>
        </div>
        <div className="form-field">
          <label>Portal URL</label>
          <input type="url" value={portalUrl} onChange={e => setPortalUrl(e.target.value)} placeholder="https://partners.…" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Agreement start</label>
          <input type="date" value={agreementStart} onChange={e => setAgreementStart(e.target.value)} />
        </div>
        <div className="form-field">
          <label>Agreement end</label>
          <input type="date" value={agreementEnd} onChange={e => setAgreementEnd(e.target.value)} />
        </div>
      </div>
      <div className="form-field">
        <label>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="MDF rules, named contacts, special terms…" />
      </div>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </Dialog>
  );
}

// PartnerInfoCard — slotted into the AccountDetail page when the
// account's type is Partner. Lets the user edit the partner-specific
// fields (tier, commission, agreement, MDF, portal, notes) inline
// without leaving the account page. Renders nothing when no partner
// extension row exists yet (the user can add one from Sales -> Partners).
function PartnerInfoCard({ account }) {
  const existing = (D.PARTNERS || []).find(p => p.accountId === account.id);
  const [tier, setTier]         = useState(existing?.tier || "");
  const [partnerType, setPType] = useState(existing?.partnerType || "");
  const [defaultPct, setDefPct] = useState(existing?.defaultCommissionPct ?? "");
  const [mdfBudget, setMdf]     = useState(existing?.mdfBudget ?? "");
  const [aStart, setAStart]     = useState(existing?.agreementStart || "");
  const [aEnd, setAEnd]         = useState(existing?.agreementEnd   || "");
  const [portalUrl, setPortal]  = useState(existing?.portalUrl || "");
  const [notes, setNotes]       = useState(existing?.notes     || "");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [savedAt, setSavedAt]   = useState(null);

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await tpAPI.partners.save(account.id, {
        tier:                  tier || null,
        partnerType:           partnerType || null,
        defaultCommissionPct:  defaultPct === "" ? null : Number(defaultPct),
        mdfBudget:             mdfBudget === ""  ? null : Number(mdfBudget),
        agreementStart:        aStart || null,
        agreementEnd:          aEnd   || null,
        portalUrl:             portalUrl.trim() || null,
        notes:                 notes.trim()     || null,
      });
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || "Failed to save partner details.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Partner details</div>
        <div className="card-sub">Tier, commission, agreement and MDF. {existing ? "Edit and save to update." : "Saving here creates the partner extension row."}</div>
      </div>
      <div className="card-body">
        <div className="form-row">
          <div className="form-field">
            <label>Tier</label>
            <select value={tier} onChange={e => setTier(e.target.value)}>
              <option value="">— None —</option>
              {(D.PARTNER_TIERS || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Partner type</label>
            <select value={partnerType} onChange={e => setPType(e.target.value)}>
              <option value="">— None —</option>
              {(D.PARTNER_TYPES || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Default commission %</label>
            <input type="number" min="0" max="100" step="0.5" value={defaultPct}
                   onChange={e => setDefPct(e.target.value)} placeholder="15" />
          </div>
          <div className="form-field">
            <label>MDF budget</label>
            <input type="number" min="0" step="100" value={mdfBudget}
                   onChange={e => setMdf(e.target.value)} placeholder="25000" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Agreement start</label>
            <input type="date" value={aStart} onChange={e => setAStart(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Agreement end</label>
            <input type="date" value={aEnd} onChange={e => setAEnd(e.target.value)} />
          </div>
        </div>
        <div className="form-field">
          <label>Portal URL</label>
          <input type="url" value={portalUrl} onChange={e => setPortal(e.target.value)} placeholder="https://…" />
        </div>
        <div className="form-field">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
        {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
      </div>
      <div className="card-foot flex-between">
        <span className="muted">{savedAt ? "✓ Saved " + savedAt.toLocaleTimeString() : "—"}</span>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save partner details"}
        </button>
      </div>
    </div>
  );
}

// PartnerDealsCard — shows opportunities where this partner is attached.
// Read-only; clicking a row jumps to the opp detail where the commission
// JSON can be edited on the Commercials tab.
function PartnerDealsCard({ account, navigate }) {
  const opps = (D.OPPORTUNITIES || []).filter(o => o.partnerAccountId === account.id);
  if (opps.length === 0) {
    return (
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-head">
          <div className="card-title">Deals with this partner</div>
          <div className="card-sub">Opportunities where {account.name} is attached as the partner. None yet.</div>
        </div>
        <div className="card-body muted">
          Attach this partner on an opportunity’s <strong>Commercials</strong> tab to start tracking deals.
        </div>
      </div>
    );
  }
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">Deals with this partner</div>
        <div className="card-sub">{opps.length} opportunit{opps.length === 1 ? "y" : "ies"} attached</div>
      </div>
      <table className="tbl tbl-flush">
        <thead><tr>
          <th>Deal</th><th>Account</th><th>Stage</th>
          <th className="right">ACV</th>
          <th className="right">Partner %</th>
          <th className="right">Estimated payout</th>
        </tr></thead>
        <tbody>
          {opps.map(o => {
            const partnerPct = o.commission?.partner ?? 0;
            const payout = (o.acv || 0) * (partnerPct / 100);
            const a = getAccount(o.accountId) || {};
            return (
              <tr key={o.id} onClick={() => navigate("sales/opportunities/" + o.id)}>
                <td><span className="tbl-link">{o.name}</span></td>
                <td>{a.name || <span className="muted">—</span>}</td>
                <td><StageBadge stage={o.stage} /></td>
                <td className="right numeric">{fmtMoney(o.acv, o.currency)}</td>
                <td className="right numeric">{partnerPct}%</td>
                <td className="right numeric">{fmtMoney(payout, o.currency)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

Object.assign(window, { PartnersList, NewPartnerDialog, PartnerInfoCard, PartnerDealsCard });
